import { jsPDF } from 'jspdf';
import { Tenant, Sale, SaleItem, Product } from '@/types';
import { formatPricePerUnit, type Unit } from '@/utils/units';

// Colores de la marca
const COLORS = {
  primary: [107, 91, 79] as [number, number, number],      // taupe-600
  secondary: [139, 119, 101] as [number, number, number],  // taupe-500
  text: [55, 46, 39] as [number, number, number],          // taupe-800
  lightText: [107, 91, 79] as [number, number, number],    // taupe-600
  border: [214, 203, 189] as [number, number, number],     // cream-300
  background: [250, 247, 243] as [number, number, number], // cream-50
};

// Formatear moneda
const formatCurrency = (value: number | string | undefined): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value || 0;
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(num);
};

// Formatear fecha y hora
const formatDateTime = (dateStr: string | undefined): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Agregar cabecera con datos del tenant (con logo opcional)
const addHeader = async (doc: jsPDF, tenant: Tenant, title: string): Promise<number> => {
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 15;
  const logoSize = 25;
  let hasLogo = false;

  // Intentar cargar el logo si existe
  if (tenant.logo_url) {
    try {
      const logoUrl = `${window.location.origin}${tenant.logo_url}`;
      const img = await loadImage(logoUrl);
      if (img) {
        doc.addImage(img, 'PNG', 15, y - 5, logoSize, logoSize);
        hasLogo = true;
      }
    } catch (e) {
      // Si falla, continuar sin logo
      console.warn('No se pudo cargar el logo:', e);
    }
  }

  // Nombre del almacén (ajustar posición si hay logo)
  const textStartX = hasLogo ? 15 + logoSize + 5 : pageWidth / 2;
  const textAlign = hasLogo ? 'left' : 'center';

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text(tenant.name, textStartX, y, { align: textAlign as any });
  y += 6;

  // Datos de contacto
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.lightText);

  const contactLines: string[] = [];
  if (tenant.address) contactLines.push(tenant.address);
  if (tenant.phone) contactLines.push(`Tel: ${tenant.phone}`);

  contactLines.forEach((line) => {
    doc.text(line, textStartX, y, { align: textAlign as any });
    y += 4;
  });

  // Asegurar que y esté después del logo si hay uno
  if (hasLogo) {
    y = Math.max(y, 15 - 5 + logoSize + 5);
  }

  y += 3;

  // Línea separadora
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.line(15, y, pageWidth - 15, y);
  y += 8;

  // Título del documento
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.text);
  doc.text(title, pageWidth / 2, y, { align: 'center' });
  y += 10;

  return y;
};

// Helper para cargar imagen
const loadImage = (url: string): Promise<string | null> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } else {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

// Agregar pie de página
const addFooter = (doc: jsPDF, pageNumber: number, totalPages: number): void => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.lightText);

  // Fecha de generación
  doc.text(
    `Generado: ${formatDateTime(new Date().toISOString())}`,
    15,
    pageHeight - 10
  );

  // Número de página
  doc.text(
    `Página ${pageNumber} de ${totalPages}`,
    pageWidth - 15,
    pageHeight - 10,
    { align: 'right' }
  );
};

// ============================================================
// COMPROBANTE DE VENTA
// ============================================================
export const generateSaleReceipt = async (sale: Sale, tenant: Tenant): Promise<void> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  let y = await addHeader(doc, tenant, 'COMPROBANTE DE VENTA');

  // Información de la venta
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.text);

  // Recuadro con info
  doc.setFillColor(...COLORS.background);
  doc.roundedRect(15, y, pageWidth - 30, 28, 3, 3, 'F');
  y += 7;

  doc.setFont('helvetica', 'bold');
  doc.text('N° Comprobante:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(sale.receipt_number || `#${sale.id}`, 60, y);

  doc.setFont('helvetica', 'bold');
  doc.text('Fecha:', pageWidth / 2 + 10, y);
  doc.setFont('helvetica', 'normal');
  doc.text(formatDateTime(sale.created_at), pageWidth / 2 + 30, y);
  y += 7;

  doc.setFont('helvetica', 'bold');
  doc.text('Cliente:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(sale.customer_name || 'Consumidor Final', 60, y);

  doc.setFont('helvetica', 'bold');
  doc.text('Atendido por:', pageWidth / 2 + 10, y);
  doc.setFont('helvetica', 'normal');
  doc.text(sale.user_name || '-', pageWidth / 2 + 45, y);
  y += 7;

  doc.setFont('helvetica', 'bold');
  doc.text('Forma de pago:', 20, y);
  doc.setFont('helvetica', 'normal');
  const paymentLabels: Record<string, string> = {
    cash: 'Efectivo',
    card: 'Tarjeta',
    transfer: 'Transferencia',
    mixed: 'Mixto',
  };
  doc.text(paymentLabels[sale.payment_method] || sale.payment_method, 60, y);
  y += 15;

  // Tabla de items
  doc.setFillColor(...COLORS.primary);
  doc.rect(15, y, pageWidth - 30, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Producto/Servicio', 20, y + 5.5);
  doc.text('Cant.', pageWidth - 85, y + 5.5, { align: 'center' });
  doc.text('P. Unit.', pageWidth - 60, y + 5.5, { align: 'right' });
  doc.text('Subtotal', pageWidth - 20, y + 5.5, { align: 'right' });
  y += 12;

  doc.setTextColor(...COLORS.text);
  doc.setFont('helvetica', 'normal');

  sale.items.forEach((item: SaleItem, index: number) => {
    if (y > 250) {
      doc.addPage();
      y = 20;
    }

    if (index % 2 === 0) {
      doc.setFillColor(...COLORS.background);
      doc.rect(15, y - 4, pageWidth - 30, 8, 'F');
    }

    const productName = item.product_name || `Producto #${item.product_id}`;
    doc.text(productName.substring(0, 40), 20, y);
    doc.text(item.quantity.toString(), pageWidth - 85, y, { align: 'center' });
    doc.text(formatCurrency(item.unit_price), pageWidth - 60, y, { align: 'right' });
    doc.text(formatCurrency(item.subtotal), pageWidth - 20, y, { align: 'right' });

    if (item.discount_percentage > 0) {
      y += 5;
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.lightText);
      doc.text(`  Descuento: ${item.discount_percentage}%`, 20, y);
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.text);
    }

    y += 8;
  });

  // Línea separadora
  y += 2;
  doc.setDrawColor(...COLORS.border);
  doc.line(pageWidth - 80, y, pageWidth - 15, y);
  y += 8;

  // Totales
  doc.setFont('helvetica', 'normal');
  doc.text('Subtotal:', pageWidth - 60, y, { align: 'right' });
  doc.text(formatCurrency(sale.subtotal), pageWidth - 20, y, { align: 'right' });
  y += 6;

  if (sale.discount_amount > 0) {
    doc.setTextColor(220, 38, 38); // red-600
    doc.text(`Descuento (${sale.discount_percentage}%):`, pageWidth - 60, y, { align: 'right' });
    doc.text(`-${formatCurrency(sale.discount_amount)}`, pageWidth - 20, y, { align: 'right' });
    doc.setTextColor(...COLORS.text);
    y += 6;
  }

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL:', pageWidth - 60, y, { align: 'right' });
  doc.text(formatCurrency(sale.total), pageWidth - 20, y, { align: 'right' });
  y += 15;

  // Mensaje de agradecimiento
  doc.setFontSize(10);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...COLORS.lightText);
  doc.text('¡Gracias por su confianza!', pageWidth / 2, y, { align: 'center' });

  addFooter(doc, 1, 1);

  // Descargar
  doc.save(`comprobante-${sale.receipt_number || sale.id}.pdf`);
};

// ============================================================
// LISTADO DE PRECIOS
//
// Para mandarle a los clientes: solo nombre y precio de venta, agrupado por
// categoria. No lleva costos, margenes ni stock — es lo unico que sale del
// sistema pensado para que lo vea alguien de afuera.
// ============================================================
export const generatePriceList = async (
  products: Product[],
  tenant: Tenant,
): Promise<void> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Solo lo que se puede vender hoy y tiene precio puesto
  const vendibles = products.filter(
    (p) => p.is_active !== false && Number(p.sale_price ?? 0) > 0,
  );

  // Lista plana, alfabetica. Sin encabezados de categoria: las categorias cargadas
  // ("PRUEBA 1", "OTROS") no le dicen nada a un cliente y cortaban la lectura.
  const items = [...vendibles].sort((a, b) => a.name.localeCompare(b.name, 'es'));

  let y = await addHeader(doc, tenant, 'Listado de precios');

  const nuevaPaginaSiHaceFalta = (alto: number) => {
    if (y + alto > pageHeight - 20) {
      doc.addPage();
      y = 20;
    }
  };

  doc.setFontSize(9);
  doc.setTextColor(...COLORS.text);

  for (const p of items) {
    nuevaPaginaSiHaceFalta(7);

    const unidad = (p.display_unit as Unit) || 'unit';
    // Los precios se guardan por unidad base: un granel en $/gramo hay que
    // mostrarlo en $/kg o el cliente lee cualquier cosa.
    const precio = formatPricePerUnit(p.sale_price, unidad, unidad !== 'unit');

    doc.setFont('helvetica', 'normal');
    // Nombre recortado para que nunca pise al precio
    const nombre = doc.splitTextToSize(p.name, pageWidth - 75)[0];
    doc.text(nombre, 18, y);

    doc.setFont('helvetica', 'bold');
    doc.text(precio, pageWidth - 18, y, { align: 'right' });

    y += 6;
  }

  if (vendibles.length === 0) {
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.lightText);
    doc.text('No hay productos con precio para listar.', 18, y);
  }

  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    addFooter(doc, i, total);
  }

  const fecha = new Date().toISOString().slice(0, 10);
  doc.save(`listado-precios-${fecha}.pdf`);
};
